import { Router } from './core/router.js';
import { auth } from './core/auth.js';
import { applyTheme, loadTheme } from './core/theme.js';
import { AppShell } from './components/AppShell.js';
import { LoginView } from './views/LoginView.js';
import { PropertiesView } from './views/PropertiesView.js';
import { PropertyView } from './views/PropertyView.js';
import { UnitView } from './views/UnitView.js';
import { NewUnitWizardView } from './views/NewUnitWizardView.js';
import { NewPropertyView } from './views/NewPropertyView.js';
import { TenanciesView } from './views/TenanciesView.js';
import { MoveInView } from './views/MoveInView.js';
import { TenancyView } from './views/TenancyView.js';
import { TenantsView } from './views/TenantsView.js';
import { TenantFormView } from './views/TenantFormView.js';
import { MeView } from './views/MeView.js';
import { TasksView } from './views/TasksView.js';
import { InviteView } from './views/InviteView.js';
import { PublicPropertyView } from './views/PublicPropertyView.js';
import { PropertySheetView } from './views/PropertySheetView.js';
import { DocumentView } from './views/DocumentView.js';
import { AgencyView } from './views/AgencyView.js';
import { NotificationsView } from './views/NotificationsView.js';
import { DashboardView } from './views/DashboardView.js';
import { InquiryView } from './views/InquiryView.js';
import { LandlordInquiriesView } from './views/LandlordInquiriesView.js';
import { HandoverEditView } from './views/HandoverEditView.js';
import { TenantRequestsInboxView } from './views/TenantRequestsInboxView.js';
import { TenantRequestsOutboxView } from './views/TenantRequestsOutboxView.js';
import { TenantRequestView } from './views/TenantRequestView.js';
import { OcrLogsView } from './views/OcrLogsView.js';
import { MaintenanceView } from './views/MaintenanceView.js';

applyTheme(loadTheme());

const root = document.getElementById('app');
const shell = new AppShell(root);
shell.mount();

const PUBLIC_PATHS = ['/login'];
const PUBLIC_PREFIXES = ['/invite/', '/p/', '/c/'];

const isPublic = (path) => PUBLIC_PATHS.includes(path)
  || PUBLIC_PREFIXES.some((p) => path.startsWith(p));

const router = new Router({
  outlet: shell.outlet,
  routes: [
    { path: '/',                   component: () => redirect() },
    { path: '/login',              component: () => new LoginView() },
    { path: '/invite/:token',      component: (ctx) => new InviteView(ctx) },
    { path: '/p/:slug',            component: (ctx) => new PublicPropertyView(ctx) },
    { path: '/properties',         component: () => new PropertiesView() },
    { path: '/properties/new',     component: (ctx) => new NewPropertyView(ctx) },
    { path: '/properties/:id/wizard/:step', component: (ctx) => new NewPropertyView(ctx) },
    { path: '/properties/:id/sheet', component: (ctx) => new PropertySheetView(ctx) },
    { path: '/properties/:id/units/new', component: (ctx) => new NewUnitWizardView(ctx) },
    { path: '/properties/:id/units/:unitId', component: (ctx) => new UnitView(ctx) },
    { path: '/properties/:id',     component: (ctx) => new PropertyView(ctx) },
    { path: '/tenants',            component: () => new TenantsView() },
    { path: '/tenants/new',        component: (ctx) => new TenantFormView(ctx) },
    { path: '/tenants/:id/edit',   component: (ctx) => new TenantFormView(ctx) },
    { path: '/tenancies',          component: () => new TenanciesView() },
    { path: '/tenancies/new',      component: (ctx) => new MoveInView(ctx) },
    { path: '/tenancies/:id',      component: (ctx) => new TenancyView(ctx) },
    { path: '/documents/:id',      component: (ctx) => new DocumentView(ctx), feature: 'billing' },
    { path: '/tasks',              component: () => new TasksView() },
    { path: '/me',                 component: () => new MeView() },
    { path: '/dashboard',                         component: () => new DashboardView() },
    { path: '/agency',                            component: () => new AgencyView(), feature: 'agency' },
    { path: '/notifications',                     component: () => new NotificationsView() },
    { path: '/inquiries',                         component: (ctx) => new LandlordInquiriesView(ctx), feature: 'marketplace' },
    { path: '/inquiries/:id',                     component: (ctx) => new LandlordInquiriesView(ctx), feature: 'marketplace' },
    { path: '/c/:token',                          component: (ctx) => new InquiryView(ctx) },
    { path: '/handovers/:id',                     component: (ctx) => new HandoverEditView(ctx), feature: 'handover' },
    { path: '/maintenance',                       component: () => new MaintenanceView(), feature: 'maintenance' },
    { path: '/maintenance/:id',                   component: (ctx) => new MaintenanceView(ctx), feature: 'maintenance' },
    { path: '/tenant-requests',                   component: () => new TenantRequestsInboxView(), feature: 'tenant_requests' },
    { path: '/tenant-requests/incoming',          component: () => new TenantRequestsInboxView(), feature: 'tenant_requests' },
    { path: '/tenant-requests/outgoing',          component: () => new TenantRequestsOutboxView(), feature: 'tenant_requests' },
    { path: '/tenant-requests/:id',               component: (ctx) => new TenantRequestView(ctx), feature: 'tenant_requests' },
    { path: '/admin/ocr-logs',                    component: (ctx) => new OcrLogsView(ctx) },
  ],
  guard: (route, ctx) => {
    // Bookmarked legacy /contracts URLs (PWA cache, email links) get a
    // transparent client-side redirect to the renamed /tenancies routes.
    if (ctx.path === '/contracts') return '/tenancies';
    if (ctx.path.startsWith('/contracts/')) return '/tenancies/' + ctx.path.slice('/contracts/'.length);

    if (auth.isAuthenticated && ctx.path === '/login') return auth.defaultPath();
    if (!auth.isAuthenticated && !isPublic(ctx.path)) return '/login';
    // Pure tenants can't manage properties — bounce any /properties* deep link
    // (the nav already hides these) to their home. The public listing lives at
    // /p/:slug, so it isn't caught here. Server-side is the real authority.
    if (auth.isAuthenticated && ctx.path.startsWith('/properties') && !auth.canManageProperties) {
      return auth.defaultPath();
    }
    // Plan feature gating: routes may declare a `feature` key; users whose
    // plan lacks it get bounced home. hasFeature fails open for legacy
    // sessions without a features field — the server 403 is the authority.
    if (auth.isAuthenticated && route.feature && !auth.hasFeature(route.feature)) {
      return auth.defaultPath();
    }
    // The /select-role chooser is retired (platform roles collapsed to
    // INDIVIDUAL); send any deep link there to the default landing page.
    if (auth.isAuthenticated && ctx.path === '/select-role') return auth.defaultPath();
    return null;
  },
});

router.onChange((ctx) => shell.setActiveTab(ctx.path));
// Re-resolve the route ONLY on identity-affecting auth changes
// (sign-in/sign-out, role switch). Profile patches via patchUser
// (saving billing data, toggling marketplace, etc.) update the cached
// user but should NOT remount every view on the current page — that
// caused panels like the bank-accounts list to refetch every time the
// user hit "Save" on Számlázási adatok.
let _lastAuthKey = identityKey(auth);
auth.subscribe(() => {
  const next = identityKey(auth);
  if (next !== _lastAuthKey) {
    _lastAuthKey = next;
    router._resolve();
  }
});
function identityKey(a) {
  return JSON.stringify({
    u: a.user?.id || null,
    r: a.activeRole || null,
    auth: !!a.isAuthenticated,
  });
}
router.start();

// Bridge "/" to the right place after the router has booted.
function redirect() {
  return {
    render() {
      window.location.hash = '#' + auth.defaultPath();
      return document.createElement('div');
    },
    destroy() {},
  };
}
